/**
 * Validity checks for the "ראה תיקון מוצע" sweep: preview (עריכה מוצעת)
 * before apply, and the live Word paragraph after שלח תגובה.
 */

export const SEE_PROPOSED_CORRECTION_INNER = 'ראה תיקון מוצע';
export const SEE_PROPOSED_LOGICAL_SUFFIX = `[${SEE_PROPOSED_CORRECTION_INNER}]`;
/** Word RTL insert may store the mirrored-bracket form. */
export const SEE_PROPOSED_MIRRORED_SUFFIX = `]${SEE_PROPOSED_CORRECTION_INNER}[`;

export function foldQrCheckText(text: string): string {
  return (text || '')
    .replace(/[\u200E\u200F\u061C\u0005\u0007\r\n]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function suffixCount(folded: string): number {
  const re = /ראה תיקון מוצע/g;
  return (folded.match(re) || []).length;
}

function endsWithProposedSuffix(folded: string): boolean {
  return (
    folded.endsWith(SEE_PROPOSED_LOGICAL_SUFFIX) ||
    folded.endsWith(SEE_PROPOSED_MIRRORED_SUFFIX) ||
    folded.endsWith(SEE_PROPOSED_CORRECTION_INNER)
  );
}

function garbledSuffix(folded: string): boolean {
  if (!folded.includes(SEE_PROPOSED_CORRECTION_INNER)) return false;
  return (
    folded.includes(`[[${SEE_PROPOSED_CORRECTION_INNER}`) ||
    folded.includes(`${SEE_PROPOSED_CORRECTION_INNER}]]`) ||
    folded.includes(`]]${SEE_PROPOSED_CORRECTION_INNER}`) ||
    folded.includes(`${SEE_PROPOSED_CORRECTION_INNER}[[`) ||
    /ראה תיקון מוצע\s*ראה תיקון מוצע/.test(folded)
  );
}

export function evaluateSeeProposedPreview(args: {
  previewText: string;
  baselineText: string;
  insBlue: number;
  htmlHasTrackChangeNew: boolean;
  insTexts?: string[];
}): { ok: boolean; reasons: string[] } {
  const reasons: string[] = [];
  const preview = foldQrCheckText(args.previewText);
  const baseline = foldQrCheckText(args.baselineText);
  const insJoined = foldQrCheckText((args.insTexts || []).join(' '));

  if (!preview) reasons.push('preview_empty');
  if (/לא נמצא|not found/i.test(baseline) && baseline.length < 40) {
    reasons.push('preview_unusable_clause');
  }
  if (baseline.length >= 8 && endsWithProposedSuffix(baseline)) {
    reasons.push('preview_baseline_already_has_suffix');
  }
  if (!preview.includes(SEE_PROPOSED_CORRECTION_INNER)) {
    reasons.push('preview_missing_suffix');
  }
  if (suffixCount(preview) > 1) reasons.push('preview_duplicate_suffix');
  if (garbledSuffix(preview)) reasons.push('preview_garbled_suffix');
  if (!endsWithProposedSuffix(preview) && preview.includes(SEE_PROPOSED_CORRECTION_INNER)) {
    reasons.push('preview_suffix_not_at_end');
  }
  if (!args.htmlHasTrackChangeNew && Number(args.insBlue) <= 0) {
    reasons.push('preview_no_blue_insertion');
  }
  if (insJoined && !insJoined.includes(SEE_PROPOSED_CORRECTION_INNER)) {
    reasons.push('preview_blue_ins_is_not_the_suffix');
  }
  if (baseline.length >= 24) {
    const head = baseline.slice(0, Math.min(40, baseline.length));
    if (head.length >= 20 && !preview.includes(head)) {
      reasons.push('preview_lost_baseline_head');
    }
  }
  if (baseline.length >= 40 && preview.length < Math.floor(baseline.length * 0.6)) {
    reasons.push('preview_truncated_vs_baseline');
  }

  return { ok: reasons.length === 0, reasons };
}

export function evaluateSeeProposedApplyFromSnapshot(args: {
  textHead: string;
  textTail: string;
  textLen: number;
  baselineText: string;
  hasUndo: boolean;
  tableOk?: boolean;
}): { ok: boolean; reasons: string[] } {
  return evaluateSeeProposedApply({
    liveAfter: foldQrCheckText(args.textTail),
    liveHead: args.textHead,
    baselineText: args.baselineText,
    hasUndo: args.hasUndo,
    tableOk: args.tableOk,
    liveLen: args.textLen,
  });
}

export function evaluateSeeProposedApply(args: {
  liveAfter: string;
  baselineText: string;
  hasUndo: boolean;
  tableOk?: boolean;
  liveLen?: number;
  liveHead?: string;
}): { ok: boolean; reasons: string[] } {
  const reasons: string[] = [];
  const live = foldQrCheckText(args.liveAfter);
  const liveHead = foldQrCheckText(args.liveHead || live);
  const baseline = foldQrCheckText(args.baselineText);
  const liveLen = args.liveLen ?? live.length;
  // Snapshot head/tail of a short paragraph are the same string — do not count the suffix twice.
  const liveForCount =
    !live || !liveHead || liveHead === live || liveHead.includes(live) || live.includes(liveHead)
      ? live || liveHead
      : `${liveHead} ${live}`;

  if (!live && !liveHead) reasons.push('apply_empty');
  if (!live.includes(SEE_PROPOSED_CORRECTION_INNER) && !liveHead.includes(SEE_PROPOSED_CORRECTION_INNER)) {
    reasons.push('apply_missing_suffix');
  }
  if (suffixCount(liveForCount) > 1) reasons.push('apply_duplicate_suffix');
  if (garbledSuffix(live) || garbledSuffix(liveHead)) reasons.push('apply_garbled_suffix');
  if (live.includes(SEE_PROPOSED_CORRECTION_INNER) && !endsWithProposedSuffix(live)) {
    reasons.push('apply_suffix_not_at_end');
  }
  if (baseline.length >= 24) {
    const head = baseline.slice(0, Math.min(40, baseline.length));
    const headMissing =
      head.length >= 20 && !liveHead.includes(head.slice(0, 20)) && !live.includes(head);
    if (headMissing) {
      // Card CR can be a longer extract than All-Markup live (track_changes_116:
      // CR 693 vs live 255). Accept when the distinctive CR tail is present.
      const tail = baseline.slice(-Math.min(40, baseline.length));
      const tailOk =
        tail.length >= 24 &&
        (live.includes(tail.slice(0, 20)) || liveHead.includes(tail.slice(0, 20)));
      if (!tailOk) reasons.push('apply_lost_baseline_head');
    }
  }
  if (baseline.length > 0 && liveLen <= baseline.length) {
    // Same longer-CR case: suffix append grows the live paragraph even when the
    // card baseline string is longer than the live Word text.
    const suffixOk = endsWithProposedSuffix(live) || endsWithProposedSuffix(liveHead);
    if (!suffixOk) reasons.push('apply_length_did_not_grow');
  }
  if (!args.hasUndo) reasons.push('apply_no_undo_button');
  if (args.tableOk === false) reasons.push('apply_table_broken');

  return { ok: reasons.length === 0, reasons };
}
